import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { ArrowLeft, Mail, CreditCard, Eye, FileText, User as UserIcon, Smartphone } from "lucide-react";
import { AdminShell } from "@/components/AdminShell";
import { Button } from "@/components/ui/button";
import { DateCell } from "@/components/admin/DateCell";
import { StatusBadge } from "@/components/admin/StatusBadge";
import { ProviderBadge, RoleBadges } from "@/components/admin/ProviderBadge";
import { PlanDialog, type PlanSavePayload } from "@/components/admin/PlanDialog";
import { adminGetUser, adminSetUserPlan } from "@/lib/admin.functions";
import { PLANS, type PlanId } from "@/lib/plans";
import { categoryLabel } from "@/lib/categories";
import { UserActivity } from "@/components/admin/UserActivity";
import { UserPushDevices } from "@/components/admin/UserPushDevices";
import { UserEmails } from "@/components/admin/UserEmails";

export const Route = createFileRoute("/_authenticated/admin/anvandare_/$userId")({
  head: () => ({
    meta: [
      { title: "Användare — Administration | Sellavo" },
      {
        name: "description",
        content: "Detaljer, kontotyp, betalningar och annonser för en användare.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: () => (
    <AdminShell title="Användarprofil">
      <UserBody />
    </AdminShell>
  ),
});

function planName(plan: string) {
  return PLANS[(plan as PlanId) in PLANS ? (plan as PlanId) : "free"].name;
}

/**
 * Vad användaren faktiskt betalar. En nivå som vi lagt till i administrationen
 * är ingen betalning – då visas det tydligt utan belopp.
 */
function paymentLabel(u: {
  plan: string;
  currentPeriodEnd: string | null;
  planPaid?: boolean;
  planSource?: string | null;
  planBilling?: string | null;
}) {
  if (u.plan === "free") return "Betalar inget (Gratis)";
  const expired = u.currentPeriodEnd
    ? new Date(u.currentPeriodEnd).getTime() < Date.now()
    : false;
  if (expired) return "Utgången – ingen betalning";
  if (!u.planPaid) {
    return u.planSource === "admin" ? "Gåva från oss – ingen betalning" : "Obetald";
  }
  const def = (PLANS as any)[u.plan];
  const price =
    u.planBilling === "yearly" ? (def?.priceYearLabel ?? "årsvis") : (def?.priceLabel ?? "");
  return `Betalar ${price}`;
}

/** Visar användarens val av utseende i klartext. */
function themeLabel(mode: string | null) {
  if (mode === "light") return "Ljust läge";
  if (mode === "dark") return "Mörkt läge";
  if (mode === "auto") return "Följer solen (automatiskt)";
  return "Inte valt (följer solen)";
}

function Info({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-border p-3">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <div className="mt-1 text-sm text-foreground">{children}</div>
    </div>
  );
}

function UserBody() {
  const { userId } = Route.useParams();
  const qc = useQueryClient();
  const fetchUser = useServerFn(adminGetUser);
  const setPlanFn = useServerFn(adminSetUserPlan);
  const [editingPlan, setEditingPlan] = useState(false);
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["admin-user", userId],
    // Ett avbrutet svar (t.ex. omladdning mitt i) får aldrig lämna tom data till vyn.
    queryFn: async () => {
      const res = await fetchUser({ data: { userId } });
      if (!res) throw new Error("Kunde inte hämta användaren. Försök igen.");
      return res;
    },
    retry: 1,
  });

  const savePlan = useMutation({
    mutationFn: (vars: PlanSavePayload) =>
      setPlanFn({ data: { userId, ...vars } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-user", userId] });
      qc.invalidateQueries({ queryKey: ["admin-users-paged"] });
      setEditingPlan(false);
    },
    onError: (e: Error) => toast.error(e.message || "Kunde inte spara kontotypen."),
  });

  if (isLoading) return <p className="text-muted-foreground">Laddar…</p>;
  if (error instanceof Error || !data)
    return (
      <div className="space-y-3">
        <p className="text-destructive">
          {error instanceof Error ? error.message : "Kunde inte hämta användaren."}
        </p>
        <Button variant="secondary" disabled={isFetching} onClick={() => refetch()}>
          Försök igen
        </Button>
      </div>
    );


  const u = data as any;

  return (
    <div className="space-y-8">
      <Link
        to="/admin/anvandare"
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Alla användare
      </Link>

      <section className="rounded-md border border-border p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground">
              <UserIcon className="h-5 w-5 text-primary" />
              {u.displayName || "Inget användarnamn"}
            </h2>
            <p className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
              <Mail className="h-3.5 w-3.5" /> {u.email}
              {!u.confirmed && (
                <span className="ml-1 rounded bg-muted px-1.5 py-0.5 text-[11px]">obekräftad</span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded bg-primary/10 px-3 py-1 text-sm font-semibold text-primary">
              <CreditCard className="h-4 w-4" /> {planName(u.plan)}
            </span>
            <Button variant="outline" size="sm" onClick={() => setEditingPlan(true)}>
              Ändra kontotyp
            </Button>
            <Button asChild variant="outline" size="sm" className="gap-1.5">
              <Link to="/admin/visa-som/$userId" params={{ userId }}>
                <Eye className="h-4 w-4" /> Visa som
              </Link>
            </Button>
          </div>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Info label="Registrerad">
            <DateCell value={u.createdAt} />
          </Info>
          <Info label="Senast inloggad">
            <DateCell value={u.lastSignInAt} />
          </Info>
          <Info label="Giltig till">
            {u.currentPeriodEnd ? (
              <DateCell value={u.currentPeriodEnd} />
            ) : u.plan === "free" ? (
              "–"
            ) : (
              "Tills vidare"
            )}
          </Info>
          <Info label="Betalning">{paymentLabel(u)}</Info>
          <Info label="Annonser">{u.adsCount}</Info>

          <Info label="Roll">
            <RoleBadges roles={u.roles} />
          </Info>
          <Info label="Inloggningssätt">
            <span className="flex flex-col gap-1">
              <ProviderBadge provider={u.provider} />
              {u.providerSince && (
                <span className="text-[11px] text-muted-foreground">
                  Kopplat <DateCell value={u.providerSince} />
                </span>
              )}
            </span>
          </Info>

          <Info label="Kontotyp ändrad">
            <DateCell value={u.planSince} />
          </Info>
          <Info label="Cookies">
            {u.cookieConsentAt
              ? u.cookieAnalytics
                ? "Godkänt analys"
                : "Endast nödvändiga"
              : "Inte svarat"}
          </Info>
          <Info label="Cookie-val gjort">
            <DateCell value={u.cookieConsentAt} />
          </Info>
          <Info label="Godkänt villkor">
            {u.termsAcceptedAt ? (
              <span>
                v{u.termsVersion} · <DateCell value={u.termsAcceptedAt} />
              </span>
            ) : (
              "Nej"
            )}
          </Info>
          <Info label="Utseende">{themeLabel(u.themeMode)}</Info>
          <Info label="Aviseringar">
            {[
              u.notifyMessages === false ? null : "Meddelanden",
              u.notifyAdExpiry === false ? null : "Annons går ut",
            ]
              .filter(Boolean)
              .join(" · ") || "Avstängda"}
          </Info>
          <Info label="Användar-ID">
            <span className="break-all text-xs text-muted-foreground">{u.id}</span>
          </Info>
        </div>

      </section>

      <section>
        <h3 className="flex items-center gap-2 font-heading text-base font-semibold">
          <CreditCard className="h-4 w-4 text-primary" /> Köp och kontotypsändringar
        </h3>
        {u.planHistory.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">
            Inga registrerade ändringar av kontotyp ännu.
          </p>
        ) : (
          <div className="mt-3 overflow-x-auto rounded-md border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="whitespace-nowrap px-3 py-2">Datum</th>
                  <th className="whitespace-nowrap px-3 py-2">Från</th>
                  <th className="whitespace-nowrap px-3 py-2">Till</th>
                  <th className="whitespace-nowrap px-3 py-2">Period</th>
                  <th className="whitespace-nowrap px-3 py-2">Giltig till</th>
                  <th className="whitespace-nowrap px-3 py-2">Betalt</th>
                  <th className="whitespace-nowrap px-3 py-2">Källa</th>
                </tr>
              </thead>
              <tbody>
                {u.planHistory.map((h: any) => (
                  <tr key={h.id} className="border-t border-border">
                    <td className="whitespace-nowrap px-3 py-2">
                      <DateCell value={h.created_at} />
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">
                      {planName(h.old_plan ?? "free")}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 font-medium">{planName(h.new_plan)}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">
                      {h.months ? `${h.months} mån` : "–"}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">
                      {h.period_end ? <DateCell value={h.period_end} /> : "–"}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2">
                      <span
                        className={`rounded px-2 py-0.5 text-xs font-semibold ${
                          h.paid
                            ? "bg-emerald-500/15 text-emerald-600"
                            : "bg-muted text-muted-foreground"
                        }`}
                      >
                        {h.paid ? "Betalt" : "Ej betalt"}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">{h.source}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section>
        <h3 className="flex items-center gap-2 font-heading text-base font-semibold">
          <FileText className="h-4 w-4 text-primary" /> Annonser ({u.ads.length})
        </h3>
        {u.ads.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">Användaren har inga annonser.</p>
        ) : (
          <div className="mt-3 overflow-x-auto rounded-md border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="whitespace-nowrap px-3 py-2">Rubrik</th>
                  <th className="whitespace-nowrap px-3 py-2">Kategori</th>
                  <th className="whitespace-nowrap px-3 py-2">Ort</th>
                  <th className="whitespace-nowrap px-3 py-2">Pris</th>
                  <th className="whitespace-nowrap px-3 py-2">Skapad</th>
                  <th className="whitespace-nowrap px-3 py-2">Status</th>
                  <th className="whitespace-nowrap px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {u.ads.map((ad: any) => (
                  <tr key={ad.slug} className="border-t border-border hover:bg-muted/40">
                    <td className="whitespace-nowrap px-3 py-2">
                      <Link
                        to="/a/$slug"
                        params={{ slug: ad.slug }}
                        className="font-medium hover:underline"
                      >
                        {ad.title}
                      </Link>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">
                      {categoryLabel(ad.category ?? "ovrigt")}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">{ad.location || "–"}</td>
                    <td className="whitespace-nowrap px-3 py-2">{Number(ad.price).toLocaleString("sv-SE")} kr</td>
                    <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">
                      <DateCell value={ad.created_at} />
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">
                      <StatusBadge
                        label={ad.is_paused ? "Pausad" : "Aktiv"}
                        cls={ad.is_paused ? "bg-amber-500/15 text-amber-600" : "text-xs text-muted-foreground"}
                      />
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right">
                      <Link
                        to="/admin/annons/$slug"
                        params={{ slug: ad.slug }}
                        className="text-sm font-medium text-primary hover:underline"
                      >
                        Historik
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section>
        <h3 className="flex items-center gap-2 font-heading text-base font-semibold">
          <Smartphone className="h-4 w-4 text-primary" /> Enheter ({(u.devices ?? []).length})
        </h3>
        {(u.devices ?? []).length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">Inga registrerade enheter.</p>
        ) : (
          <div className="mt-3 overflow-x-auto rounded-md border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="whitespace-nowrap px-3 py-2">Plattform</th>
                  <th className="whitespace-nowrap px-3 py-2">Enhetsnamn</th>
                  <th className="whitespace-nowrap px-3 py-2">App-version</th>
                  <th className="whitespace-nowrap px-3 py-2">Senast sedd</th>
                  <th className="whitespace-nowrap px-3 py-2">Tillagd</th>
                </tr>
              </thead>
              <tbody>
                {u.devices.map((d: any) => (
                  <tr key={d.id} className="border-t border-border">
                    <td className="whitespace-nowrap px-3 py-2 font-medium capitalize">{d.platform}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">
                      {d.device_name || "–"}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">
                      {d.app_version || "–"}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">
                      <DateCell value={d.last_seen_at} />
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">
                      <DateCell value={d.created_at} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <UserActivity data={u.activity} />

      <UserPushDevices userId={userId} />

      <UserEmails userId={userId} />

      <PlanDialog
        user={
          editingPlan
            ? {
                id: u.id,
                email: u.email,
                plan: u.plan,
                current_period_end: u.currentPeriodEnd ?? null,
              }
            : null
        }
        onClose={() => setEditingPlan(false)}
        onSave={(payload) => savePlan.mutate(payload)}
        saving={savePlan.isPending}
        error={savePlan.error instanceof Error ? savePlan.error.message : null}
      />
    </div>
  );
}
